/* ===== Shared Utility Types: Literal Unions, Constants, Guards ===== */
/* Split from types.ts — leaf node, 0 imports from other shared modules */

// --- Error Codes ---

/** Error codes for i18n — server returns code, client displays localized message */
export const ErrorCodes = {
  // Page
  PAGE_NAME_INVALID: 'PAGE_NAME_INVALID',
  PAGE_NOT_FOUND: 'PAGE_NOT_FOUND',
  PAGE_LIMIT: 'PAGE_LIMIT',
  // Location
  LINK_NOT_FOUND: 'LINK_NOT_FOUND',
  TARGET_PAGE_NOT_FOUND: 'TARGET_PAGE_NOT_FOUND',
  GM_ONLY_NAVIGATION: 'GM_ONLY_NAVIGATION',
  NAVIGATE_REQUEST_NOT_FOUND: 'NAVIGATE_REQUEST_NOT_FOUND',
  // Token
  TOKEN_NOT_FOUND: 'TOKEN_NOT_FOUND',
  TOKEN_MOVE_FORBIDDEN: 'TOKEN_MOVE_FORBIDDEN',
  // Sheet
  SHEET_NOT_FOUND: 'SHEET_NOT_FOUND',
  SHEET_NAME_INVALID: 'SHEET_NAME_INVALID',
  // Alert
  ALERT_LIMIT: 'ALERT_LIMIT',
  ALERT_NAME_INVALID: 'ALERT_NAME_INVALID',
  ALERT_TRIGGER_LIMIT: 'ALERT_TRIGGER_LIMIT',
  // Pin
  PIN_NOT_FOUND: 'PIN_NOT_FOUND',
  PIN_LIMIT: 'PIN_LIMIT',
  // Journal
  JOURNAL_NAME_INVALID: 'JOURNAL_NAME_INVALID',
  // Text
  TEXT_NOT_FOUND: 'TEXT_NOT_FOUND',
  TEXT_LIMIT: 'TEXT_LIMIT',
  TEXT_INVALID: 'TEXT_INVALID',
  // Map Object
  MAP_OBJECT_NOT_FOUND: 'MAP_OBJECT_NOT_FOUND',
  MAP_OBJECT_LIMIT: 'MAP_OBJECT_LIMIT',
  MAP_OBJECT_INVALID: 'MAP_OBJECT_INVALID',
  // Wall
  WALL_NOT_FOUND: 'WALL_NOT_FOUND',
  WALL_LIMIT: 'WALL_LIMIT',
  WALL_INVALID: 'WALL_INVALID',
  WALL_NOT_DOOR: 'WALL_NOT_DOOR',
  WALL_CHAIN_MIN_POINTS: 'WALL_CHAIN_MIN_POINTS',
  WALL_AUTO_MANAGED: 'WALL_AUTO_MANAGED',
  // Generic
  LIMIT_EXCEEDED: 'LIMIT_EXCEEDED',
  NOT_FOUND: 'NOT_FOUND',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
} as const;

export type ErrorCode = (typeof ErrorCodes)[keyof typeof ErrorCodes];

// --- Text ---

export type TextFontSize = 'small' | 'medium' | 'large';

// --- Character Sheet Enums ---

export type FieldType = 'number' | 'text' | 'boolean' | 'select' | 'list';
export type RestType = 'short' | 'long' | 'none';
export type SheetType = 'pc' | 'npc';

// --- Token Actions & AoE ---

export type TokenActionCategory = 'attack' | 'spell' | 'ability' | 'other';
export type AoeShape = 'cone' | 'sphere' | 'cube' | 'line' | 'cylinder';

// --- Roll Card ---

export type RollCardDisclosure = 'total' | 'breakdown' | 'audit';
export type RollCardVariant = 'default' | 'attack' | 'death-save' | 'concentration';

// --- Active Effects ---

export type EffectMode = 'add' | 'multiply' | 'override' | 'upgrade' | 'downgrade' | 'set-proficiency' | 'advantage' | 'disadvantage';

// --- Token & Annotation Enums ---

export type TokenFilter = 'players' | 'npcs' | 'all' | string;
export type TerrainType = 'normal' | 'difficult' | 'hazard' | 'wall' | 'water' | 'elevation';
export type CoverType = 'none' | 'half' | 'three_quarters' | 'full';

// --- Map Objects ---

export type MapObjectCategory =
  | 'furniture'
  | 'terrain'
  | 'hazard'
  | 'door'
  | 'container'
  | 'light'
  | 'custom';

export type ObjectCover = 'half' | 'three_quarter' | 'full';
export type ObjectState = 'closed' | 'open' | 'broken' | 'lit' | 'extinguished';

// --- Display Settings ---

export type MeasurementMode = 'simple' | 'alternating' | 'euclidean' | 'hex';
export type GridStyle = 'lines' | 'dots' | 'none';
export type HpBarVisibility = 'always' | 'hover' | 'gm-only' | 'never';
export type TokenSize = 'tiny' | 'small' | 'medium' | 'large' | 'huge' | 'gargantuan';

// --- Player Permissions ---

export type ViewOtherSheets = 'none' | 'basic' | 'full';

// --- Roles ---

export type Role = 'gm' | 'player';

// --- Map Notes ---

export type NoteIcon = 'pin' | 'scroll' | 'skull' | 'chest' | 'star' | 'question' | 'exclamation' | 'camp' | 'sword' | 'key';

// --- Walls ---

export type WallBlockingType =
  | 'wall'       // Блокирует видимость и движение
  | 'door'       // Закрытая = wall, открытая = пропускает
  | 'window'     // Блокирует движение, пропускает свет (частично)
  | 'terrain'    // Блокирует видимость, не движение
  | 'secret'     // Невидима для игроков (секретная дверь)
  | 'ethereal';  // Прозрачная для особых условий

export type DoorState = 'closed' | 'open' | 'locked';

// --- Journal ---

export type JournalEntryType = 'npc' | 'location' | 'quest' | 'faction' | 'handout' | 'lore';
export type JournalVisibility = 'gm' | 'all' | 'personal';
export type QuestStatus = 'active' | 'completed' | 'failed' | 'hidden';
export type NpcDisposition = 'friendly' | 'neutral' | 'hostile' | 'unknown';

// --- Alerts ---

export type AlertLayout = 'fullscreen' | 'banner-top' | 'banner-bottom';

// --- Homebrew ---

export type HomebrewEntryType = 'class' | 'subclass' | 'spell' | 'feat' | 'race' | 'item';

// --- Timeline ---

export type TimeOfDay = 'dawn' | 'morning' | 'midday' | 'afternoon' | 'dusk' | 'evening' | 'night' | 'midnight';

export type TimelineEventType =
  | 'session-start'
  | 'session-recap'
  | 'encounter'
  | 'discovery'
  | 'plot'
  | 'travel'
  | 'rest'
  | 'downtime'
  | 'character'
  | 'custom'
  | 'chapter';

export type TimelineEventStatus = 'published' | 'draft';

export type TimelineEventSource = 'manual' | 'ai-generated' | 'ai-assisted' | 'system';

export type TimelineLinkTarget = 'journal' | 'sheet' | 'pin';

export type CampaignSessionStatus = 'planned' | 'active' | 'completed';

// --- Game System ---

/** Supported game system identifiers */
export type GameSystemId = 'dnd5e' | 'pf2e' | 'generic';

/** Array of valid GameSystemId values for runtime validation */
export const GAME_SYSTEM_IDS: readonly GameSystemId[] = ['dnd5e', 'pf2e', 'generic'] as const;

/** Type guard: check if a value is a valid GameSystemId */
export function isValidGameSystem(value: unknown): value is GameSystemId {
  return typeof value === 'string' && (GAME_SYSTEM_IDS as readonly string[]).includes(value);
}

/** Convenience helper: check if session is D&D 5e */
export function isDnd5eSession(state: { gameSystem: GameSystemId }): boolean {
  return state.gameSystem === 'dnd5e';
}

// --- NPC Behavior ---

/** Тактический профиль поведения NPC для AI-инструментов */
export type NpcBehaviorProfile = 'aggressive' | 'cautious' | 'supportive';

// --- Push Notifications ---

/** Типы push-событий */
export type PushEventType = 'initiative:turn-changed' | 'page:navigate' | 'alert:play';

// --- AI Telemetry Types ---

export type AiOutcome = 'success' | 'error' | 'timeout' | 'aborted';
export type AiComplexityClass = 'trivial' | 'simple' | 'moderate' | 'complex' | 'heavy';
export type AiToolLevel = 'L0' | 'L1' | 'L2';
export type AiIncidentType = 'tool_failure' | 'loop' | 'timeout' | 'data_leak';
export type AiIncidentSeverity = 'low' | 'medium' | 'high' | 'critical';
export type AiAbortStage = 'thinking' | 'streaming' | 'tool_execution';

export type AiBehavioralCategory =
  | 'AI_JOURNEY_BREAK'
  | 'AI_SEMANTIC_DRIFT'
  | 'AI_CONTEXT_BLINDNESS'
  | 'AI_SCOPE_VIOLATION'
  | 'AI_DATA_LEAK_TOOL'
  | 'AI_DATA_LEAK_TEXT'
  | 'AI_RULES_ERROR'
  | 'AI_STATE_CORRUPTION'
  | 'AI_TOOL_MISUSE'
  | 'AI_RESOURCE_WASTE'
  | 'AI_LOOP_STALL'
  | 'AI_LOOP_DIVERGENCE';

// --- AI Context Enums ---

export type CampaignTone = 'gritty' | 'balanced' | 'lighthearted' | string;
export type AiPersonaType = 'builtin' | 'journal' | 'sheet';

// --- Persona Emotions ---

/** 7 эмоций Тии. Эфемерные — не персистятся в SessionState. */
export type PersonaEmotion = 'neutral' | 'interested' | 'amused' | 'impressed' | 'concerned' | 'nostalgic' | 'bored';

/** Валидные значения PersonaEmotion (для runtime validation) */
export const PERSONA_EMOTIONS: readonly PersonaEmotion[] = [
  'neutral', 'interested', 'amused', 'impressed', 'concerned', 'nostalgic', 'bored',
] as const;

// --- Voice ---

/** Статус подключения Discord-бота к голосовому каналу */
export type VoiceConnectionStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'stt-unavailable'
  | 'error';

// --- Import/Export ---

export type ImportSource = 'roll20' | 'foundry' | 'dndbeyond' | 'pdf' | 'image' | 'text';
export type ConflictStrategy = 'overwrite' | 'rename' | 'skip';
export type ImportWizardStep = 'source' | 'upload' | 'conflicts' | 'confirm';

// --- Campaign Text Import ---

/** Entity type for campaign text import */
export type CampaignEntityType = 'npc' | 'location' | 'note' | 'journal' | 'encounter';

// --- Player Permissions Default ---

export interface PlayerPermissions {
  moveOtherTokens: boolean;
  drawOnMap: boolean;
  viewOtherSheets: ViewOtherSheets;
  createNotes: boolean;
  useAi: boolean;
  addTokens: boolean;
}

export const DEFAULT_PLAYER_PERMISSIONS: PlayerPermissions = {
  moveOtherTokens: false,
  drawOnMap: true,
  viewOtherSheets: 'basic',
  createNotes: true,
  useAi: true,
  addTokens: false,
};

// --- Resource Limits ---

export const LIMITS = {
  MAX_TOKENS_PER_PAGE: 300,
  MAX_DRAWINGS_PER_PAGE: 1000,
  MAX_FOG_REVEALS_PER_PAGE: 2000,
  MAX_FOG_BATCH_POINTS: 200,
  MAX_FOG_CLOSINGS_PER_PAGE: 2000,
  MAX_POINTS_PER_STROKE: 5000,
  MAX_SHEETS_PER_SESSION: 100,
  MAX_PAGES_PER_SESSION: 100,
  MAX_JOURNAL_ENTRIES_PER_SESSION: 200,
  MAX_LOCATION_LINKS_PER_PAGE: 50,
  PENDING_NAVIGATION_TTL_MS: 5 * 60 * 1000,
  // AI Context limits
  MAX_WORLD_DESCRIPTION_CHARS: 2000,
  MAX_HOUSE_RULES_CHARS: 1000,
  MAX_GM_INSTRUCTIONS_CHARS: 500,
  MAX_EXCLUDED_ENTITIES: 50,
  MAX_SECTION_PRIORITY: 10,
  MAX_BACKSTORY_CHARS: 1500,
  MAX_PERSONALITY_CHARS: 500,
  MAX_PLAYER_NOTES_CHARS: 500,
  MAX_SESSION_FOCUS_CHARS: 300,
  MAX_ACTIVE_NPCS: 10,
  MAX_SPOILER_GUARD: 20,
  // Annotation & Trigger limits
  MAX_ANNOTATIONS_PER_PAGE: 100,
  MAX_TRIGGERS_PER_PAGE: 20,
  MAX_TRIGGER_ACTIONS: 10,
  MAX_ANNOTATION_LABEL_CHARS: 100,
  MAX_ANNOTATION_DESCRIPTION_CHARS: 500,
  MAX_TRIGGER_LABEL_CHARS: 100,
  /** SG-001 — bound activeEffects array on a single sheet (DoS via repeated effect-add). */
  MAX_ACTIVE_EFFECTS_PER_SHEET: 100,
  /** SG-002 — bound rows in a single repeating section (DoS via repeated row-add). */
  MAX_REPEATING_ROWS_PER_SECTION: 50,
  /** BF-06 — polygon vertex cap shared by fog reveals / closings / trigger geometry. */
  MAX_POLYGON_POINTS: 500,
  /** BF-07 — tokenIds array cap on `all_tokens_dead` trigger condition. */
  MAX_CONDITION_TOKEN_IDS: 200,
  // Text limits
  MAX_TEXT_OBJECTS_PER_PAGE: 200,
  MAX_TEXT_LENGTH: 500,
  MAX_BATCH_DUPLICATE_TOKENS: 20,
  // Map Object limits
  MAX_MAP_OBJECTS_PER_PAGE: 200,
  MAX_MAP_OBJECT_LABEL_CHARS: 200,
  MAX_MAP_OBJECT_DESCRIPTION_CHARS: 2000,
  // Wall limits (Dynamic Lighting)
  MAX_WALLS_PER_PAGE: 1000,
  WALL_CHAIN_MIN_POINTS: 2,
  // Drawing selection limits
  MAX_SELECTED_STROKES: 100,
  // Batch action limits
  MAX_BATCH_ACTIONS: 20,
  MAX_BATCH_DELAY_MS: 2000,
  // Tactical & Location Navigation limits
  MAX_FORMATION_TOKENS: 20,
  MAX_SETUP_LOCATION_TOKENS: 10,
  // Lighting limits (Dynamic Lighting Advanced)
  MAX_LIGHT_SOURCES: 50,
  // Vision limits (Dynamic Lighting Advanced)
  MAX_VISION_TOKENS: 20,
  // Homebrew limits (Spec 049-B)
  MAX_HOMEBREW_PACKAGES_PER_USER: 50,
  MAX_HOMEBREW_ENTRIES_PER_PACKAGE: 200,
  MAX_HOMEBREW_PDF_SIZE_BYTES: 100 * 1024 * 1024,  // 100 MB
  MAX_HOMEBREW_TEXT_LENGTH: 100_000,
  // Timeline limits (Spec 053)
  MAX_TIMELINE_EVENTS: 1000,
  MAX_CAMPAIGN_SESSIONS: 100,
  MAX_TIMELINE_TAGS: 20,
  MAX_TIMELINE_LINKS_PER_EVENT: 10,
  MAX_TIMELINE_TITLE_CHARS: 200,
  MAX_TIMELINE_DESCRIPTION_CHARS: 5000,
  MAX_CALENDAR_MONTHS: 30,
  MAX_CALENDAR_MONTH_DAYS: 100,
  MAX_CALENDAR_WEEK_DAYS: 14,
} as const;

/**
 * Базовый промпт карты (заказ владельца 02.10.2026: «у мастера должен быть способ поменять базовый промт,
 * можно просто вставлять автоматически в поле для ввода»). Подставляется в поля окна карты, кисти и
 * расширения; мастер правит его как обычный текст. Сервер свой текст к запросу мастера не дописывает.
 * «Без текста» — прогоны 02.10: Nano Banana 2 и другие вписывали в карту подписи комнат и заголовки.
 */
export const MAP_BASE_PROMPT = 'Strict top-down view, tabletop RPG battle map, no text, no labels, no legend. ';
